<template>
  <div class="ds-page">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Companies</div>
          <h2 class="page-title">MyBenefit Request</h2>
          <p class="page-subtitle">Review and approve MyBenefit package assignment requests</p>
        </div>
        <div class="col-auto ms-auto">
          <button class="ds-btn ds-btn--secondary" @click="searchHandler"><i class="el-icon-refresh" /> Refresh</button>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="myr-filter-header">
          <span class="ds-filters__label">Filters</span>
        </div>
        <div class="myr-filter-two-col">
          <!-- Left: filter fields (2-col inner grid) -->
          <div class="myr-filter-left">
            <div class="myr-filter-grid-left">
              <div class="ds-field">
                <label class="ds-field__label">Filter By</label>
                <el-select v-model="filter.search_by" size="small" class="w-100" @change="filter.search_keyword = ''">
                  <el-option v-for="(item, index) in search_options" :key="index" :value="item.value" :label="item.text" />
                </el-select>
              </div>

              <div class="ds-field" v-if="filter.search_by === 'company_id'">
                <label class="ds-field__label">Company</label>
                <el-select
                  v-model="filter.search_keyword"
                  filterable
                  remote
                  :remote-method="getCompanyRequestor"
                  size="small"
                  class="w-100"
                  placeholder="Select company"
                >
                  <el-option v-for="(item, index) in company_list" :key="index" :value="item.value" :label="item.text" />
                </el-select>
              </div>

              <div class="ds-field" v-else>
                <label class="ds-field__label">Package</label>
                <el-select v-model="filter.search_keyword" size="small" class="w-100" placeholder="Select package">
                  <el-option v-for="(item, index) in package_list" :key="index" :value="item.value" :label="item.text" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Approval Status</label>
                <el-select v-model="filter.status_filter" size="small" class="w-100">
                  <el-option v-for="(item, index) in status_options" :key="index" :value="item.value" :label="item.text" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="myr-filter-sep" />

          <!-- Right: action buttons -->
          <div class="myr-filter-right">
            <div class="myr-filter-actions">
              <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="resetFilter">Reset</button>
              <button class="ds-btn ds-btn--blue ds-btn--sm" @click="searchHandler">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <div class="ds-table-wrap" v-loading="loading.data">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Request Date</th>
              <th>Requestor</th>
              <th>Company</th>
              <th>Package</th>
              <th>Effective Date</th>
              <th>Status</th>
              <th>Approval Date</th>
              <th>Approved By</th>
              <th v-if="canApproveMyBen">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in list" :key="index">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ dsFormatDateTime(item.created_at) }}</span>
              </td>
              <td>{{ item.user_requestor ? item.user_requestor.name : "System" }}</td>
              <td>
                <span class="ds-text-bold">{{ item.company ? item.company.company_name : "—" }}</span>
              </td>
              <td>
                <div class="ds-myben-cell" v-if="item.mybenefit_package">
                  <img :src="item.mybenefit_package.package_logo" class="ds-myben-logo" />
                  <span>{{ item.mb_package }}</span>
                </div>
                <span v-else class="ds-text-muted">—</span>
              </td>
              <td>{{ dsFormatDate(item.mybenefit_start_at) }}</td>
              <td>
                <span class="ds-pill" :class="mybenRequestStatusClass(item.status)">
                  {{ item.status === "request" ? "Waiting Approval" : dsStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <span class="ds-text-sm ds-text-muted">{{ approvalDate(item) }}</span>
              </td>
              <td>{{ item.user_approval ? item.user_approval.name : "—" }}</td>
              <td v-if="canApproveMyBen">
                <div class="myr-action-group">
                  <button
                    class="ds-btn ds-btn--sm ds-btn--success ds-btn--icon"
                    :disabled="item.status !== 'request'"
                    @click="handlerBtnApproval(item, 'approve')"
                    title="Approve"
                  >
                    <font-awesome-icon icon="check" />
                  </button>
                  <button
                    class="ds-btn ds-btn--sm ds-btn--danger ds-btn--icon"
                    :disabled="item.status !== 'request'"
                    @click="handlerBtnApproval(item, 'reject')"
                    title="Reject"
                  >
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <el-empty v-if="total_rows === 0" description="No data found" :image="emptyStateImage" class="ds-empty" />
      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>
  </div>
</template>

<script>
import { uniqBy } from "lodash";
import {
  GET_MYBENEFIT_REQUEST,
  APPROVAL_MYBENEFIT_REQUEST,
  GET_MYBENEFIT_REQUEST_COMPANY,
  GET_MYBENEFIT,
} from "@/store/modules/mybenefit";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import Pagination from "../../components/Pagination.vue";
import popupErrorMessages from "../../../library/popup-error-messages";

export default {
  components: { Pagination },
  name: "MyBenefitRequest",
  mixins: [designSystemMixin],
  metaInfo: {
    title: "MyBenefit Request",
  },
  data() {
    return {
      loading: {
        data: false,
      },
      emptyStateImage,
      total_rows: 0,
      list: [],
      page: 1,
      per_page: 20,
      filter: {
        search_by: "company_id",
        search_keyword: "",
        status_filter: "",
      },
      search_options: [
        { text: "Company", value: "company_id" },
        { text: "MyBenefit Package", value: "mybenefit_package_id" },
      ],
      company_list: [{ value: "", text: "All" }],
      package_list: [{ value: "", text: "All" }],
      status_options: [
        { value: "", text: "All" },
        { value: "request", text: "Waiting Approval" },
        { value: "approved", text: "Approved" },
        { value: "rejected", text: "Rejected" },
      ],
      timeout: {
        filter_company: null,
      },
    };
  },
  async mounted() {
    const loader = this.$loading.show();
    await this.getList();
    this.getCompanyRequestor();
    this.loadMyBenefitList();
    loader.hide();
  },
  computed: {
    currentMyProfile() {
      return this.$store.getters.currentMyProfile;
    },
    currentRole() {
      return this.$store.getters.current_role;
    },
    canApproveMyBen() {
      return this.currentRole.page_name === "*" || this.currentMyProfile.is_checker;
    },
  },
  methods: {
    async getList() {
      this.loading.data = true;
      if (this.filter.search_keyword === "" && this.filter.search_by === "company_id") {
        this.getCompanyRequestor();
      }
      try {
        const { count, rows } = await this.$store.dispatch(GET_MYBENEFIT_REQUEST, {
          page: this.page,
          ...this.filter,
        });
        this.total_rows = count;
        this.list = rows.map((v) => {
          v.mb_package = v.mybenefit_package ? v.mybenefit_package.package_name : "—";
          return v;
        });
      } finally {
        this.loading.data = false;
      }
    },
    approvalDate(item) {
      if (item.status === "approved") return this.dsFormatDateTime(item.approved_at);
      if (item.status === "rejected") return this.dsFormatDateTime(item.rejected_at);
      return "—";
    },
    mybenRequestStatusClass(status) {
      if (String(status).toLowerCase() === "request") return "ds-pill--warning";
      return this.dsStatusClass(status);
    },
    resetFilter() {
      this.filter.search_by = "company_id";
      this.filter.search_keyword = "";
      this.filter.status_filter = "";
      this.page = 1;
      this.getList();
    },
    pageChangeHandler(value) {
      this.page = value;
      this.getList();
    },
    handlerBtnApproval(item, act) {
      let msg = "Are you sure want to approve this request? Package will active based on effective date.";
      if (act === "reject") {
        msg = "Are you sure want to reject this request?";
      }
      this.$confirm(msg, "Confirmation", {
        type: "info",
        confirmButtonText: "Yes",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(APPROVAL_MYBENEFIT_REQUEST, {
                id: item.id,
                action: act,
              });
              this.$message({
                title: "Success",
                type: "success",
                message: "Approval success",
              });
              this.getList();
              done();
            } catch (err) {
              popupErrorMessages(err.response.data);
            } finally {
              instance.confirmButtonLoading = false;
            }
          }
          done();
        },
      });
    },
    async getCompanyRequestor(value = "") {
      clearTimeout(this.timeout.filter_company);
      this.timeout.filter_company = setTimeout(async () => {
        const { rows } = await this.$store.dispatch(GET_MYBENEFIT_REQUEST_COMPANY, {
          search_by: "company_name",
          search_keyword: value,
        });
        this.company_list = this.company_list.filter((v) => v.value === "" || v.value === this.filter.search_keyword);
        this.company_list.push(...rows.map((v) => ({ value: v.id, text: v.company_name })));
        this.company_list = uniqBy(this.company_list, "value");
      }, 1000);
    },
    async loadMyBenefitList() {
      this.loading.package_list = true;
      await this.$store.dispatch(GET_MYBENEFIT).then((response) => {
        this.package_list.splice(1, this.package_list.length);
        this.package_list.push(...response.rows.map((v) => ({ value: v.id, text: v.package_name })));
      });
      this.loading.package_list = false;
    },
    searchHandler() {
      this.page = 1;
      this.getCompanyRequestor();
      this.getList();
    },
  },
};
</script>

<style scoped>
/* Filter layout */
.myr-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}
.myr-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}
.myr-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
}
.myr-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.myr-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}
.myr-filter-header {
  margin-bottom: 12px;
}

/* MyBenefit package cell */
.ds-myben-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ds-myben-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;
}

/* Text helpers */
.ds-text-sm {
  font-size: 0.8125rem;
}
.ds-text-muted {
  color: #6c757d;
}
.ds-text-bold {
  font-weight: 600;
}

/* Action buttons */
.myr-action-group {
  display: flex;
  gap: 6px;
}
.ds-btn--success {
  background: #2fb344;
  border-color: #2fb344;
  color: #fff;
}
.ds-btn--success:hover:not(:disabled) {
  background: #25a03c;
  border-color: #25a03c;
}
.ds-btn--danger {
  background: #d63939;
  border-color: #d63939;
  color: #fff;
}
.ds-btn--danger:hover:not(:disabled) {
  background: #bf2f2f;
  border-color: #bf2f2f;
}
.ds-btn--icon {
  padding: 0.25rem 0.5rem;
  line-height: 1;
}
</style>
